import { useEffect, useMemo, useState } from 'react'
import { Abrufausfall, useAbruf, useHintergrundabruf } from './Abruf'
import { useListenwahl } from './Listenwahl'
import { api } from './api'
import { fachlicherSatz } from './Fehlersatz'
import { Auswahlfeld, mitCode } from './Auswahlfeld'
import { beschriftung, periodenname, t } from './sprache'
import { Periodenfeld } from './Periodenfeld'
import { useDarf } from './Rechte'
import { AUFRISSSTUFE, einzug } from './Bericht'
import { Baumzeile, berichtsstand } from './Gliederungsbaum'
import type { Beschriftung, Gliederungsbild, Gliederungsknoten, Positionsplan } from './typen'
import { Zeilenmenue } from './Zeilenmenue'
import { Reihenfolgefenster } from './Reihenfolgefenster'
import type { Reihenfolgeziel } from './Reihenfolgefenster'

/** Ersatz, wenn der Wahlvorrat der Pläne im Hintergrund nicht kam (R1212): keine Wahl. */
const KEINE_PLAENE: Positionsplan[] = []

/**
 * Die Gliederung umbauen — **ziehen oder anklicken** (7.4 Abschnitt 12,
 * R319–R322).
 *
 * **Gezogen wird dieselbe Zeile, die das Blatt schreibt.** Eine Position hängt
 * unter einer anderen, weil in `position_zuordnung` eine Zeile mit Eltern,
 * Sortierung und Gültigkeitszeitraum steht. Der Griff ist eine bequemere Hand
 * am selben Modell und kein zweiter Weg (R317) — wer lieber tippt, pflegt
 * dieselben Felder unten im Blatt.
 *
 * **Zwei Absichten, die nicht dasselbe sind** (R319): *Umgliedern* heißt „ab
 * dieser Periode steht das woanders", *Korrigieren* heißt „das war schon immer
 * so gemeint". Der Schalter dafür steht über dem Baum und nicht in einem
 * Rückfragefenster: Er gilt für alles, was man in dieser Sitzung anfasst.
 *
 * **Jeder Griff geht auch ohne Maus** (R306): Anklicken greift, das zweite
 * Anklicken legt ab. Das Gegriffene ist am Rahmen zu erkennen, nicht nur an
 * der Farbe (R143).
 */
export function Positionsgliederung({
  ledger,
  ledgerwahl,
  aufLedger,
  periode,
  aufAuswahl,
  aufAenderung,
}: {
  ledger: string
  /**
   * Die Bewertungsebenen zur Wahl — **seit dem 2026-09-18 am Bildschirm**
   * (R611).
   *
   * Bis dahin kam die Ebene aus der Kopfzeile des Programms. Die steht jetzt
   * nur noch über einem Bericht, und ein Bildschirm, der eine Angabe braucht,
   * führt sie selbst — wie die Feldzeile eines Laufs (R609).
   */
  ledgerwahl: { wert: string; text: string; suchtext?: string }[]
  aufLedger: (schluessel: string) => void
  periode: number
  /** Eine angeklickte Position im Blatt darunter aufschlagen (R317). */
  aufAuswahl?: (schluessel: string) => void
  aufAenderung?: () => void
}) {
  const [abPeriode, setAbPeriode] = useState(periode)
  const [korrektur, setKorrektur] = useState(false)
  const [gegriffen, setGegriffen] = useState<Griff | null>(null)
  const [einfuegenAn, setEinfuegenAn] = useState<string | null>(null)
  const [meldung, setMeldung] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [stand, setStand] = useState(0)
  const [menue, setMenue] = useState<{ x: number; y: number; ziel: Reihenfolgeziel } | null>(null)
  const [reihenfolge, setReihenfolge] = useState<Reihenfolgeziel | null>(null)

  /*
    **Was der Angemeldete nicht darf, ist nicht da** (8.1 Abschnitt 7, R1180):
    Ohne Recht wird nichts gegriffen, gezogen oder eingefügt — der Baum steht
    zum Lesen da.
  */
  const darf = useDarf()
  const darfPosition = darf('positionUmhaengen')
  const darfKonto = darf('kontoUmhaengen')
  const darfEinfuegen = darf('positionEinfuegen')

  useEffect(() => setAbPeriode(periode), [periode])

  // Der Wahlvorrat der Pläne: ein Nachschlagen im Hintergrund (7.2 Abschnitt 30, R1007; R1211).
  const plaene = useHintergrundabruf(() => api.positionsplaene(), [stand], KEINE_PLAENE)

  /**
   * Welcher Positionsplan gezeigt wird — **leer heißt: der Vorgabeplan** (R614).
   *
   * **Bis zum 2026-09-18 gab es die Wahl nicht**, und damit den zweiten Plan
   * einer Ebene nicht: Für `LDG-IFRS` gelten `PLA-HGB` und `PLA-IFRS`, der
   * Dienst nimmt ohne Angabe den mit dem Vorgabekennzeichen — also immer
   * `PLA-HGB`. `PLA-IFRS` ließ sich pflegen, nur nicht aufschlagen.
   *
   * **Die Wahl gehört zur Bewertungsebene** (7.2 Abschnitt 48, R1504). Bis zum
   * 2026-10-09 fiel ein Plan, der für die neue Ebene nicht gilt, erst nach dem
   * Wechsel ab — dazwischen fragte der Baum nach HGB mit `PLA-IFRS`. Jetzt ist
   * er im selben Augenblick fort; ein Plan beider Ebenen bleibt stehen.
   */
  const [plan, setPlan] = useListenwahl(ledger, '', (p) =>
    plaene.some((x) => x.schluessel === p && x.ledger.includes(ledger)),
  )

  /*
    **Die Pläne einer Ebene, in der Reihenfolge, in der sie gelten** — der
    Vorgabeplan zuerst, weil er es ohne Wahl auch wäre.
  */
  const planwahl = useMemo(
    () =>
      plaene
        .filter((p) => p.ledger.includes(ledger))
        .sort((a, b) => Number(b.istVorgabe) - Number(a.istVorgabe))
        .map((p) => ({
          wert: p.schluessel,
          text: mitCode(p.code, beschriftung(p.namen), ' — ', p.schluessel),
          suchtext: [p.schluessel, p.code, ...Object.values(p.namen)].join(' '),
        })),
    [plaene, ledger],
  )

  /*
    **Ein Plan, der für diese Ebene nicht gilt, fällt weg.** Wer von IFRS auf
    HGB wechselt, während `PLA-IFRS` steht, bekommt sonst einen Baum, den es
    für seine Ebene nicht gibt.
  */
  useEffect(() => {
    if (plan && planwahl.length > 0 && !planwahl.some((p) => p.wert === plan)) {
      setPlan('')
    }
  }, [planwahl, plan])

  /*
    **Der Baum ist ein Hauptabruf** (7.2 Abschnitt 39, R1211). `null` heißt
    seit dem 2026-10-06 „kein Positionsplan" — vorher kam dafür „nicht
    gefunden", und ein Ausfall des Dienstes sah genauso aus.
  */
  const bildabruf = useAbruf<Gliederungsbild | null>(
    ledger ? () => api.gliederung(ledger, abPeriode, plan || undefined) : null,
    [ledger, abPeriode, plan, stand],
  )
  const bild = bildabruf.wert

  /*
    **Offen wie im Bericht** (7.2 Abschnitt 55, R1857): jede Position mit
    Unterpositionen, die Konten erst auf Klick. Bis zum 2026-10-16 stand der
    Baum hier ganz ausgerollt, jedes Konto als Marke unter seiner Position.
    Gesetzt wird das, wenn Ebene oder Plan wechseln — nicht nach jedem Zug,
    sonst klappte jedes Umhängen den Baum wieder auf.
  */
  const [aufgeklappt, setAufgeklappt] = useState<ReadonlySet<string> | null>(null)
  useEffect(() => setAufgeklappt(null), [ledger, plan])
  useEffect(() => {
    if (bild && aufgeklappt === null) {
      setAufgeklappt(berichtsstand(bild.knoten))
    }
  }, [bild, aufgeklappt])

  function klappe(schluessel: string) {
    setAufgeklappt((bisher) => {
      const neu = new Set(bisher ?? [])
      if (neu.has(schluessel)) {
        neu.delete(schluessel)
      } else {
        neu.add(schluessel)
      }
      return neu
    })
  }

  /** Was zu sehen ist: eine Zeile, deren Oberpositionen alle offen sind. */
  const zeilen = useMemo(() => {
    const knoten = bild?.knoten ?? []
    const offen = aufgeklappt ?? berichtsstand(knoten)
    const pfad: Gliederungsknoten[] = []
    return knoten.flatMap((k, i) => {
      while (pfad.length > 0 && (pfad[pfad.length - 1]?.ebene ?? 0) >= k.ebene) {
        pfad.pop()
      }
      const sichtbar = pfad.every((a) => offen.has(a.schluessel))
      pfad.push(k)
      if (!sichtbar) {
        return []
      }
      const naechste = knoten[i + 1]
      const hatUnter = naechste !== undefined && naechste.ebene > k.ebene
      return [{ k, offen: offen.has(k.schluessel), hatKinder: hatUnter || k.konten.length > 0 }]
    })
  }, [bild, aufgeklappt])

  // Wer unter wem hängt, steht nicht im Bild — es kommt flach und mit Ebene.
  // Für das Ablegen „vor dieses Geschwister" wird beides gebraucht: der Vater
  // der Zeile und die Zeile selbst.
  const vaeter = useMemo(() => vaterVon(bild?.knoten ?? []), [bild])

  async function tue(was: () => Promise<unknown>) {
    setLaeuft(true)
    setMeldung(null)

    try {
      await was()
      setGegriffen(null)
      setEinfuegenAn(null)
      setStand((n) => n + 1)
      aufAenderung?.()
    } catch (fehler) {
      // Befund mit Handreichung (R145); eine Störung meldet das
      // Störungsfenster (7.2 Abschnitt 30, R1003/R1004).
      setMeldung(fachlicherSatz(fehler))
    } finally {
      setLaeuft(false)
    }
  }

  /** Ablegen: unter eine Position (`vor` fehlt) oder zwischen zwei (`vor`). */
  function lege(eltern: string | null, vor: string | null) {
    const griff = gegriffen
    if (!griff || laeuft) {
      return
    }

    if (griff.art === 'konto') {
      // Ein Konto kennt keine Reihenfolge: Es hängt an einer Position, und
      // welche Stelle es in ihrer Liste einnimmt, sagt seine Nummer.
      const ziel = vor ? vaeter.get(vor) ?? eltern : eltern
      if (!ziel) {
        return
      }

      void tue(() => api.kontoUmhaengen(griff.schluessel, ziel, abPeriode, korrektur))
      return
    }

    void tue(() =>
      api.positionUmhaengen(
        griff.schluessel,
        vor ? vaeter.get(vor) ?? null : eltern,
        vor,
        abPeriode,
        korrektur,
      ),
    )
  }

  if (bildabruf.ausgefallen) {
    return <Abrufausfall abruf={bildabruf} />
  }

  if (!bild) {
    return bildabruf.laedt ? null : <p className="hinweis">{t('gliederung.keinplan')}</p>
  }

  return (
    <section className="gliederungsbaum">
      <div className="berichtsleiste">
        {/*
          **Der Plan steht als Überschrift da, wo es nur einen gibt — und als
          Wahl, wo es mehrere gibt** (R614, R293): Ein Auswahlfeld mit einem
          Eintrag ist ein Bedienelement, das nichts bedient; eine Überschrift,
          die einen von zwei Plänen nennt, verschweigt den anderen.
        */}
        {planwahl.length <= 1 && <h2>{beschriftung(bild.plannamen)}</h2>}

        {/* Die Ebene, deren Pläne hier zur Wahl stehen — sie gehört zum
            Bildschirm und nicht an den Kopf des Programms (R611). */}
        <label className="suchzeile">
          <span className="name">{t('kontext.ledger')}</span>
          <Auswahlfeld
            wert={ledger}
            eintraege={ledgerwahl}
            beschriftung={t('kontext.ledger')}
            aufWahl={aufLedger}
          />
        </label>

        {/*
          **Und welcher ihrer Pläne** (R614): Für eine Ebene können mehrere
          gelten — `LDG-IFRS` führt den handelsrechtlichen und den nach
          Fristigkeit. Ohne diese Wahl war immer der Vorgabeplan gemeint, und
          der zweite ließ sich nicht aufschlagen. Eine Ebene mit nur einem Plan
          zeigt ihn hier ohne Alternative; das ist keine Frage, sondern eine
          Auskunft darüber, was man gerade vor sich hat.
        */}
        {planwahl.length > 1 && (
          <label className="suchzeile">
            <span className="name">{t('gliederung.plan')}</span>
            <Auswahlfeld
              wert={plan || (planwahl[0]?.wert ?? '')}
              eintraege={planwahl}
              beschriftung={t('gliederung.plan')}
              aufWahl={setPlan}
            />
          </label>
        )}

        {/* MM.JJJJ wie jede Periodeneingabe in der Anwendung (R502). */}
        <Periodenfeld
          wert={abPeriode}
          aufWert={(p) => p !== null && setAbPeriode(p)}
          beschriftung={t('gliederung.abperiode')}
          gesperrt={korrektur}
        />

        {/*
          Der Schalter sagt, was ein Zug bedeutet — und er steht sichtbar da,
          weil der Unterschied nicht im Griff zu sehen ist: Beides sieht aus
          wie Ziehen und ist es nicht (R319).
        */}
        {(darfPosition || darfKonto) && (
          <label className="suchzeile">
            <input
              type="checkbox"
              checked={korrektur}
              onChange={(e) => setKorrektur(e.target.checked)}
            />
            <span className="name">{t('gliederung.korrektur')}</span>
          </label>
        )}
      </div>

      {(darfPosition || darfKonto) && (
        <p className="erlaeuterung">
          {t(korrektur ? 'gliederung.korrektur.erlaeuterung' : 'gliederung.umgliederung.erlaeuterung', {
            // **Im Satz steht der Monat, im Feld die Feldform** (7.2 Abschnitt 21,
            // R839 — Befund vom 2026-09-29): Hier stand „gilt ab 12.2026". Das
            // ist die Schreibweise zum Tippen (R502); gelesen wird „Dezember 2026".
            periode: periodenname(abPeriode),
          })}
        </p>
      )}

      {meldung && <p className="hinweis">{meldung}</p>}

      {gegriffen && (
        <p className="hinweis griffzeile">
          {t('gliederung.gegriffen', { gegenstand: gegriffen.name })}{' '}
          <button type="button" className="schmal" onClick={() => setGegriffen(null)}>
            {t('gliederung.loslassen')}
          </button>
        </p>
      )}

      {/* **Gezeichnet wie der Bericht** (7.2 Abschnitt 55, R1855, R1856): Rang
          statt des Worts „Summe", das Konto als Zeile mit „·" statt als Marke. */}
      <ul className="baum berichtsbaum">
        {zeilen.flatMap(({ k, offen, hatKinder }) => [
          /*
            Die Lücke zwischen zwei Zeilen ist eine eigene Trefferfläche:
            „unter A" und „vor A.II." sind zwei verschiedene Aussagen, und
            ohne die Lücke ließe sich die zweite nur über ein Zahlenfeld
            treffen.
          */
          gegriffen !== null && gegriffen.schluessel !== k.schluessel ? (
            <li key={`${k.schluessel}:luecke`} className="lueckenzeile">
              <Luecke
                sichtbar
                ebene={k.ebene}
                einrueckung={einzug(k.ebene)}
                aufAblegen={() => lege(null, k.schluessel)}
                beschriftung={t('gliederung.vor', { position: zeilenname(k) })}
              />
            </li>
          ) : null,

          <Baumzeile
            key={k.schluessel}
            art="position"
            ebene={k.ebene}
            rang={k}
            klasse={zeilenklasse(k, gegriffen)}
            pfeil={{
              offen,
              hatKinder,
              beschriftung: t(offen ? 'gliederungswahl.zuklappen' : 'gliederungswahl.aufklappen', {
                position: zeilenname(k),
              }),
              aufKlappen: () => klappe(k.schluessel),
            }}
            draggable={darfPosition}
            onDragStart={(e) => {
              e.dataTransfer.setData('text/plain', k.schluessel)
              setGegriffen({ art: 'position', schluessel: k.schluessel, name: zeilenname(k) })
            }}
            onDragEnd={() => setGegriffen(null)}
            // **Rechtsklick → Reihenfolge ändern** (7.4 Abschnitt 32, R1341):
            // innerhalb der Ebene. Das Ziehen bleibt für das Umhängen.
            onContextMenu={(e) => {
              if (!darf('reihenfolgeSetzen')) return
              e.preventDefault()
              setMenue({ x: e.clientX, y: e.clientY, ziel: { art: 'positionsgliederung', element: k.schluessel, titel: zeilenname(k) } })
            }}
            onDragOver={(e) => gegriffen && e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault()
              lege(k.schluessel, null)
            }}
          >
            {/* Der Griff greift eine Position oder legt ein gegriffenes Konto ab (R1180). */}
            {(darfPosition || gegriffen !== null) && (
              <button
                type="button"
                className="griff"
                aria-pressed={gegriffen?.schluessel === k.schluessel}
                title={t('gliederung.greifen')}
                onClick={() =>
                  gegriffen
                    ? lege(k.schluessel, null)
                    : setGegriffen({ art: 'position', schluessel: k.schluessel, name: zeilenname(k) })
                }
              >
                {gegriffen && gegriffen.schluessel !== k.schluessel ? t('gliederung.hierunter') : '⠿'}
              </button>
            )}

            {/*
              **Der Schlüssel steht nicht vor dem Namen** (R699, Auftrag vom
              2026-09-22). Seit R676 ist `code` der Schlüssel, und die Nummer
              ist mit R678 vorn in die Bezeichnung gezogen — hier stand sie
              damit ZWEIMAL: *„POS-A-I-3 · A.I.3 Geschäfts- oder
              Firmenwert"*. Wer den Schlüssel sucht, findet ihn am Blatt
              darunter als Objekt-ID und über dessen Suche, die ihn mitliest.
            */}
            <button type="button" className="zeileninhalt poscode" onClick={() => aufAuswahl?.(k.schluessel)}>
              <span className="name">{zeilenname(k)}</span>
            </button>

            {darfEinfuegen && (
              <button
                type="button"
                className="schmal einfuegen"
                onClick={() => setEinfuegenAn(einfuegenAn === k.schluessel ? null : k.schluessel)}
              >
                {t('gliederung.einfuegen')}
              </button>
            )}
          </Baumzeile>,

          einfuegenAn === k.schluessel && darfEinfuegen ? (
            <li key={`${k.schluessel}:einfuegen`} className="einfuegezeile">
              <Einfuegeblatt
                unter={zeilenname(k)}
                ebene={k.ebene}
                laeuft={laeuft}
                aufAnlegen={(name) =>
                  void tue(() => api.positionEinfuegen(bild.plan, name, k.schluessel, null, abPeriode))
                }
                aufAbbruch={() => setEinfuegenAn(null)}
              />
            </li>
          ) : null,

          ...(offen
            ? k.konten.map((konto) => (
                <Baumzeile
                  key={`${k.schluessel}:${konto.schluessel}`}
                  art="konto"
                  ebene={k.ebene}
                  klasse={gegriffen?.schluessel === konto.schluessel ? 'gegriffen' : undefined}
                >
                  {/* Ohne Recht zum Umhängen steht das Konto nur da (R1180). */}
                  {!darfKonto ? (
                    <span className="zeileninhalt">
                      <span className="name">{kontoname(konto)}</span>
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="zeileninhalt kontogriff"
                      draggable
                      aria-pressed={gegriffen?.schluessel === konto.schluessel}
                      onDragStart={(e) => {
                        e.dataTransfer.setData('text/plain', konto.schluessel)
                        setGegriffen({ art: 'konto', schluessel: konto.schluessel, name: kontoname(konto) })
                      }}
                      onDragEnd={() => setGegriffen(null)}
                      onClick={() =>
                        setGegriffen(
                          gegriffen?.schluessel === konto.schluessel
                            ? null
                            : { art: 'konto', schluessel: konto.schluessel, name: kontoname(konto) },
                        )
                      }
                    >
                      <span className="name">{kontoname(konto)}</span>
                    </button>
                  )}
                </Baumzeile>
              ))
            : []),
        ])}
      </ul>

      {menue && (
        <Zeilenmenue
          x={menue.x}
          y={menue.y}
          punkte={[{ schluessel: 'reihenfolge.menue', tun: () => { setReihenfolge(menue.ziel); setMenue(null) } }]}
          aufSchliessen={() => setMenue(null)}
        />
      )}

      {reihenfolge && (
        <Reihenfolgefenster
          ziel={reihenfolge}
          aufSchliessen={(geaendert) => {
            setReihenfolge(null)
            if (geaendert) setStand((n) => n + 1)
          }}
        />
      )}
    </section>
  )
}

/* --------------------------------------------------------------------------
   Die Teile
   -------------------------------------------------------------------------- */

type Griff = { art: 'position' | 'konto'; schluessel: string; name: string }

/**
 * Wie eine Zeile **in einem Satz** heißt (R699).
 *
 * Seit R676 ist `code` der Schlüssel — `POS-A-I-3`. In der Spalte links mag er
 * stehen; in einem Satz an den Anwender steht er nicht: *„Die neue Position
 * entsteht unter POS-A-I-3"* nennt etwas, das auf keinem Papier steht und in
 * keiner Bilanz vorkommt. Die Bezeichnung trägt die Nummer ohnehin vorn
 * (R678) — *„A.I.3 Geschäfts- oder Firmenwert"* ist die Antwort auf „unter
 * was?".
 */
function zeilenname(k: Gliederungsknoten): string {
  return beschriftung(k.namen) || k.code
}

/**
 * Wie ein Konto am Chip heißt — **die Nummer steht in der Bezeichnung**
 * (R678, R699).
 *
 * `nummer` ist seit R676 der Schlüssel, `KTO-0100`. Vor der Bezeichnung
 * *„0100 Geschäfts- oder Firmenwert"* stand die Nummer damit zweimal, einmal
 * davon in einer Form, die auf keinem Kontoauszug vorkommt.
 */
function kontoname(konto: { nummer: string; namen: Beschriftung }): string {
  return beschriftung(konto.namen) || konto.nummer
}

/** Was der Griff an der Zeile anzeigt — den Rang setzt der Baustein (R1855). */
function zeilenklasse(k: Gliederungsknoten, gegriffen: Griff | null): string {
  return [
    gegriffen?.schluessel === k.schluessel ? 'gegriffen' : '',
    gegriffen && gegriffen.schluessel !== k.schluessel ? 'bereit' : '',
  ]
    .filter(Boolean)
    .join(' ')
}

/** Der Vater jeder Zeile — aus Ebene und Reihenfolge, wie das Bild sie liefert. */
function vaterVon(knoten: Gliederungsknoten[]): Map<string, string | null> {
  const vaeter = new Map<string, string | null>()
  const kette: string[] = []

  for (const k of knoten) {
    kette.length = k.ebene
    vaeter.set(k.schluessel, k.ebene === 0 ? null : kette[k.ebene - 1] ?? null)
    kette[k.ebene] = k.schluessel
  }

  return vaeter
}

/**
 * Die Trefferfläche zwischen zwei Zeilen.
 *
 * Sie ist nur da, solange etwas gegriffen ist: Eine Fläche, die immer
 * dazwischenliegt, macht jede Zeile schwerer zu treffen, ohne je gebraucht zu
 * werden.
 */
/**
 * Die Trefferfläche zwischen zwei Zeilen.
 *
 * **Sie gehört zu den Bauteilen der Gliederungen** (R700) und steht deshalb
 * seit dem 2026-09-22 auch der Bewegungsartenliste zur Verfügung — dieselbe
 * Fläche, derselbe Klassenname, derselbe Weg ohne Maus: Ein Klick auf den
 * Griff greift, ein Klick in die Lücke legt ab (R166).
 */
export function Luecke({
  sichtbar,
  ebene,
  einrueckung,
  aufAblegen,
  beschriftung: name,
}: {
  sichtbar: boolean
  ebene: number
  /** Die Einrückung des Baums, in dem sie steht — sonst die eigene Stufe. */
  einrueckung?: string
  aufAblegen: () => void
  beschriftung: string
}) {
  if (!sichtbar) {
    return null
  }

  return (
    <button
      type="button"
      className="luecke"
      style={{ marginLeft: einrueckung ?? `${ebene * 22 + 8}px` }}
      title={name}
      aria-label={name}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault()
        aufAblegen()
      }}
      onClick={aufAblegen}
    >
      <span>{name}</span>
    </button>
  )
}

/**
  * Anlegen und Einhängen in einem Schritt (R321).
  *
  * **Gefragt wird nach der Bezeichnung, und die trägt die Nummer** (R678,
  * berichtigt am 2026-09-22): `A.I.5 Sonstige`. Bis hierher standen zwei
  * Felder untereinander — *Code* und *Bezeichnung* —, und das erste war seit
  * dem 2026-09-21 folgenlos: Der Dienst nimmt seither nur noch die
  * Bezeichnung entgegen und zieht den Schlüssel. Der Anwender musste also ein
  * Feld ausfüllen, damit der Knopf angeht, und der Inhalt kam nirgends an.
  */
function Einfuegeblatt({
  unter,
  ebene,
  laeuft,
  aufAnlegen,
  aufAbbruch,
}: {
  unter: string
  ebene: number
  laeuft: boolean
  aufAnlegen: (name: string) => void
  aufAbbruch: () => void
}) {
  const [name, setName] = useState('')

  return (
    <div className="einfuegeblatt" style={{ marginLeft: einzug(ebene, AUFRISSSTUFE) }}>
      <p className="erlaeuterung">{t('gliederung.einfuegen.erlaeuterung', { position: unter })}</p>

      <label className="suchzeile">
        <span className="name">{t('stammdaten.neu.name')}</span>
        <input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </label>

      <p className="handreichung">
        <button
          type="button"
          className="haupt"
          disabled={laeuft || name.trim().length === 0}
          onClick={() => aufAnlegen(name.trim())}
        >
          {t('stammdaten.neu.speichern')}
        </button>
        <button type="button" onClick={aufAbbruch}>
          {t('import.schliessen')}
        </button>
      </p>
    </div>
  )
}
